import { X } from "lucide-react";
import { pct, statusBadge, rollingMonthLabels } from "../api";

export default function EmployeeProjectsModal({ employee, onClose }) {
  const [m1, m2, m3] = rollingMonthLabels(3);
  if (!employee) return null;
  const projects = (employee.projects || []).slice().sort((a, b) => b.avg - a.avg);
  const totals = projects.reduce(
    (acc, p) => ({
      apr: acc.apr + (p.apr || 0),
      may: acc.may + (p.may || 0),
      jun: acc.jun + (p.jun || 0),
    }),
    { apr: 0, may: 0, jun: 0 }
  );
  totals.avg = (totals.apr + totals.may + totals.jun) / 3;
  const badge = statusBadge(employee.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="emp-modal"
         style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}
         onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200"
             style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)" }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: "#1a3666" }} data-testid="emp-modal-name">
              {employee.name}
            </h2>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-600">
              <span><strong>CC:</strong> {employee.cc || "—"}</span>
              <span>·</span>
              <span><strong>Portfolio:</strong> {employee.portfolio || "Unassigned"}</span>
              {employee.role && <><span>·</span><span><strong>Role:</strong> {employee.role}</span></>}
              {employee.localPoc && <><span>·</span><span><strong>Local POC:</strong> {employee.localPoc}</span></>}
            </div>
            <div className="mt-2 flex items-center gap-3 text-sm">
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.color}`}>{badge.label}</span>
              <span className="text-slate-500">Avg <strong className="text-slate-900">{pct(employee.avg)}</strong></span>
              <span className="text-slate-500">{m1} <strong>{pct(employee.apr)}</strong></span>
              <span className="text-slate-500">{m2} <strong>{pct(employee.may)}</strong></span>
              <span className="text-slate-500">{m3} <strong>{pct(employee.jun)}</strong></span>
            </div>
          </div>
          <button onClick={onClose} data-testid="emp-modal-close"
                  className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500"><X size={18} /></button>
        </div>

        {/* Projects table */}
        <div className="p-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Project Allocations ({projects.length})
          </h3>
          {projects.length === 0 ? (
            <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500 text-sm">
              No project allocations recorded for this employee.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Project</th>
                    <th className="px-3 py-2 text-center w-20">{m1}</th>
                    <th className="px-3 py-2 text-center w-20">{m2}</th>
                    <th className="px-3 py-2 text-center w-20">{m3}</th>
                    <th className="px-3 py-2 text-center w-20">Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.name} className="border-t border-slate-100" data-testid="emp-project-row">
                      <td className="px-3 py-2 font-medium">{p.name}</td>
                      <td className="px-3 py-2 text-center">{pct(p.apr)}</td>
                      <td className="px-3 py-2 text-center">{pct(p.may)}</td>
                      <td className="px-3 py-2 text-center">{pct(p.jun)}</td>
                      <td className="px-3 py-2 text-center font-semibold" style={{ color: "#1a3666" }}>{pct(p.avg)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-center">{pct(totals.apr)}</td>
                    <td className="px-3 py-2 text-center">{pct(totals.may)}</td>
                    <td className="px-3 py-2 text-center">{pct(totals.jun)}</td>
                    <td className="px-3 py-2 text-center" style={{ color: "#1a3666" }}>{pct(totals.avg)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
